import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import clsx from "clsx";
import { FaCommentAlt, FaPlusSquare, FaQuestion} from "react-icons/fa";
import {Template} from "../../../components/template/template.jsx";
import useDashboardSse from "./hooks/use-dashboard-sse.js";
import useDashboardAlerts from "./hooks/use-dashboard-alerts.js";
import useDashboardChatWatch from "./hooks/use-dashboard-chat-watch.js";
import useDashboardConfiguration from "./hooks/use-dashboard-configuration.js";
import useDashboardCardInteractions from "./hooks/use-dashboard-card-interactions.js";
import {DashboardContext} from "./context/dashboard-context.js";
import {
    ATTENDANCE_SOURCE_FILTERS,
    ATTENDANCE_STATUS_FILTERS,
    GARRISON_COMPOSITION_FILTERS,
    GARRISON_INTENDED_FUNCTION_OPTIONS,
    GARRISON_SOURCE_FILTERS,
    GARRISON_STATUS_FILTERS,
    TASK_SOURCE_FILTERS,
    TASK_STATUS_FILTERS,
} from "./utils/dashboard-config.js";
import {filterAttendances, filterGarrisons, filterTasks, getGarrisonDerivedCounts} from "./utils/dashboard-filters.js";
import DashboardPanel from "./components/dashboard-panel.jsx";
import DashboardCounterGroup from "./components/dashboard-counter-group.jsx";
import DashboardSearch from "./components/dashboard-search.jsx";
import AttendanceCard from "./components/attendance-card.jsx";
import GarrisonCard from "./components/garrison-card.jsx";
import TaskCard from "./components/task-card.jsx";
import EmptyPanel from "./components/empty-panel.jsx";
import StartDispatchModal from "./components/start-dispatch-modal.jsx";
import StartSupportModal from "./components/start-support-modal.jsx";
import DispatchChoiceModal from "./components/dispatch-choice-modal.jsx";
import AttendanceAssignmentModal from "./components/attendance-assignment-modal.jsx";
import TaskActionModal from "./components/task-action-modal.jsx";
import CloseTaskModal from "./components/close-task-modal.jsx";
import GenerateBoModal from "./components/generate-bo-modal.jsx";
import AttendanceConvertTaskModal from "./components/attendance-convert-task-modal.jsx";
import TaskMoveAttendanceModal from "./components/task-move-attendance-modal.jsx";
import GarrisonInformationModal from "./components/garrison-information-modal.jsx";
import GarrisonHistoryModal from "./components/garrison-history-modal.jsx";
import GarrisonAssignmentsModal from "./components/garrison-assignments-modal.jsx";
import GarrisonScheduleModal from "./components/garrison-schedule-modal.jsx";
import GarrisonDispatchActionModal from "./components/garrison-dispatch-action-modal.jsx";
import AssignmentDispatchModal from "./components/assignment-dispatch-modal.jsx";
import CloseAttendanceModal from "./components/close-attendance-modal.jsx";
import ChangeOwnerModal from "./components/change-owner-modal.jsx";
import CloseDispatchModal from "./components/close-dispatch-modal.jsx";
import CloseGarrisonModal from "./components/close-garrison-modal.jsx";
import ChangeContingentsModal from "./components/change-contingents-modal.jsx";
import DashboardCardInformationModal from "./components/dashboard-card-information-modal.jsx";
import DashboardHelpModal from "./components/dashboard-help-modal.jsx";
import {DashboardCardActionBar, DashboardCardContextMenu} from "./components/dashboard-card-actions.jsx";
import {buildDashboardCardActions} from "./utils/dashboard-card-actions.js";
import "../../../css/dispatch/dashboard-legacy.css";
import {FaVolumeHigh, FaVolumeXmark} from "react-icons/fa6";
import SelectComponent from "../../../components/select/select-component.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useQueryClient} from "@tanstack/react-query";
import projectUtils from "../../../utils/project-utils.js";
import {DASHBOARD_EDIT_SAVED_MESSAGE} from "../../../utils/dashboard-edit-window.js";
import GarrisonService from "../../../services/dispatch/garrison-service.js";
import AttendanceService from "../../../services/dispatch/attendance-service.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const initialCardFilters = {
    status: null,
    source: null,
    search: "",
};

const initialGarrisonFilters = {
    status: null,
    source: null,
    composition: null,
    intendedFunction: "",
    search: "",
};

const normalizeDashboardId = value => value === null || value === undefined || value === "" ? null : String(value);

const Dashboard = () => {
    const navigate = navigateHook();
    const queryClient = useQueryClient();
    const {
        configuration,
        permissions,
        isLoading: isConfigurationLoading,
        isError: hasConfigurationError,
    } = useDashboardConfiguration();
    const [isCardDragging, setIsCardDragging] = useState(false);
    const {data, lastEventAt, hasReceivedAttendanceCard} = useDashboardSse(permissions, {isPaused: isCardDragging});
    const [unreadChatAttendanceIds, setUnreadChatAttendanceIds] = useState(new Set());
    const [isChatSilenced, setIsChatSilenced] = useState(() => localStorage.getItem("chatSilence") === "true");
    const knownChatAttendanceIdsRef = useRef(new Set());
    const hasLoadedChatStateRef = useRef(false);

    useDashboardAlerts({
        attendances: data.attendances,
        isReady: hasReceivedAttendanceCard,
        isChatSilenced,
    });

    const [activeAttendanceTab, setActiveAttendanceTab] = useState(permissions.attendance ? "attendance" : "task");
    const [attendanceFilters, setAttendanceFilters] = useState(initialCardFilters);
    const [taskFilters, setTaskFilters] = useState(initialCardFilters);
    const [garrisonFilters, setGarrisonFilters] = useState(initialGarrisonFilters);
    const [startDispatchPayload, setStartDispatchPayload] = useState(null);
    const [startSupportPayload, setStartSupportPayload] = useState(null);
    const [choicePayload, setChoicePayload] = useState(null);
    const [assignmentPayload, setAssignmentPayload] = useState(null);
    const [taskPayload, setTaskPayload] = useState(null);
    const [closeTaskPayload, setCloseTaskPayload] = useState(null);
    const [generateBoPayload, setGenerateBoPayload] = useState(null);
    const [cardInformationPayload, setCardInformationPayload] = useState(null);
    const [attendanceConvertTaskPayload, setAttendanceConvertTaskPayload] = useState(null);
    const [taskMoveAttendancePayload, setTaskMoveAttendancePayload] = useState(null);
    const [garrisonInformationPayload, setGarrisonInformationPayload] = useState(null);
    const [garrisonHistoryPayload, setGarrisonHistoryPayload] = useState(null);
    const [garrisonAssignmentsPayload, setGarrisonAssignmentsPayload] = useState(null);
    const [garrisonSchedulePayload, setGarrisonSchedulePayload] = useState(null);
    const [garrisonDispatchActionPayload, setGarrisonDispatchActionPayload] = useState(null);
    const [assignmentDispatchPayload, setAssignmentDispatchPayload] = useState(null);
    const [closeAttendancePayload, setCloseAttendancePayload] = useState(null);
    const [changeOwnerPayload, setChangeOwnerPayload] = useState(null);
    const [closeDispatchPayload, setCloseDispatchPayload] = useState(null);
    const [closeGarrisonPayload, setCloseGarrisonPayload] = useState(null);
    const [changeContingentsPayload, setChangeContingentsPayload] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);

    useEffect(() => {
        const refreshDashboardModals = event => {
            if (event.origin !== window.location.origin || event.data?.type !== DASHBOARD_EDIT_SAVED_MESSAGE) return;
            const message = typeof event.data.message === "string" && event.data.message.trim()
                ? event.data.message
                : "Item salvo com sucesso.";
            queryClient.invalidateQueries({queryKey: ["dispatch-dashboard"]});
            projectUtils.success(false, message);
        };

        window.addEventListener("message", refreshDashboardModals);
        return () => window.removeEventListener("message", refreshDashboardModals);
    }, [queryClient]);

    useEffect(() => {
        setUnreadChatAttendanceIds(current => {
            const next = new Set();
            const visibleAttendanceIds = new Set();

            data.attendances.forEach(attendance => {
                const attendanceId = normalizeDashboardId(attendance.attendanceId);
                if (!attendanceId) return;

                visibleAttendanceIds.add(attendanceId);

                if (current.has(attendanceId)) next.add(attendanceId);

                if (attendance.hasChat && !attendance.chatFinalized) {
                    if (!hasLoadedChatStateRef.current || !knownChatAttendanceIdsRef.current.has(attendanceId)) next.add(attendanceId);
                    knownChatAttendanceIdsRef.current.add(attendanceId);
                }
            });

            [...knownChatAttendanceIdsRef.current].forEach(attendanceId => {
                if (!visibleAttendanceIds.has(attendanceId)) knownChatAttendanceIdsRef.current.delete(attendanceId);
            });

            hasLoadedChatStateRef.current = true;

            if (next.size === current.size && [...next].every(attendanceId => current.has(attendanceId))) return current;
            return next;
        });
    }, [data.attendances]);

    const activeAttendanceIds = useMemo(
        () => data.attendances.map(attendance => normalizeDashboardId(attendance.attendanceId)).filter(Boolean),
        [data.attendances],
    );

    const openChatAttendanceId = cardInformationPayload?.type === "attendance" ? normalizeDashboardId(cardInformationPayload.item?.attendanceId) : null;

    const markChatUnread = useCallback(attendanceId => {
        setUnreadChatAttendanceIds(current => {
            if (current.has(attendanceId)) return current;
            const next = new Set(current);
            next.add(attendanceId);
            return next;
        });
    }, []);

    const chatWatch = useDashboardChatWatch({
        authUrlFor: AttendanceService.getUserChatAuth,
        openAttendanceId: openChatAttendanceId,
        activeChatIds: activeAttendanceIds,
        onNewMessage: markChatUnread,
    });

    useEffect(() => {
        if (cardInformationPayload?.type !== "attendance") return;
        const item = cardInformationPayload.item ?? {};
        const attendanceId = normalizeDashboardId(item.attendanceId);

        if (!attendanceId) return;

        setUnreadChatAttendanceIds(current => {
            if (!current.has(attendanceId)) return current;
            const next = new Set(current);
            next.delete(attendanceId);
            return next;
        });

        if (item.hasChat && !item.chatFinalized) chatWatch.trackChat(attendanceId);
    }, [cardInformationPayload, chatWatch]);

    const attendancesWithChatState = useMemo(() => data.attendances.map(attendance => {
        const attendanceId = normalizeDashboardId(attendance.attendanceId);
        const hasUnreadChat = Boolean(attendanceId && unreadChatAttendanceIds.has(attendanceId));

        return {
            ...attendance,
            hasUnreadChat,
            sourceName: hasUnreadChat ? "dispatchHelpNotRead" : attendance.sourceName,
        };
    }), [data.attendances, unreadChatAttendanceIds]);

    const attendanceSubtitles = useMemo(() => ({
        ...data.attendanceSubtitles,
        dispatchHelpNotRead: attendancesWithChatState.filter(attendance => attendance.hasUnreadChat).length,
    }), [attendancesWithChatState, data.attendanceSubtitles]);

    const dashboardData = useMemo(() => ({
        ...data,
        attendanceSubtitles,
        attendances: attendancesWithChatState,
    }), [attendanceSubtitles, attendancesWithChatState, data]);

    const toggleChatSilence = () => {
        setIsChatSilenced(current => {
            const next = !current;
            localStorage.setItem("chatSilence", String(next));
            return next;
        });
    };

    const cardInteractions = useDashboardCardInteractions({
        data: dashboardData,
        permissions,
        onDraggingChange: setIsCardDragging,
        onStartDispatch: setStartDispatchPayload,
        onStartSupport: setStartSupportPayload,
        onQueueAttendance: setAssignmentPayload,
        onChoice: payload => setChoicePayload({...payload, kind: "attendance"}),
        onTaskStart: payload => setTaskPayload({...payload, mode: "start-task"}),
        onTaskAssignment: payload => setTaskPayload({...payload, mode: "assignment-task"}),
        onTaskChoice: payload => setChoicePayload({...payload, kind: "task"}),
    });

    const canShowAttendanceTaskPanel = permissions.attendance || permissions.task;
    const canShowGarrisonPanel = permissions.garrison;
    const hasAnyPanel = canShowAttendanceTaskPanel || canShowGarrisonPanel;
    useEffect(() => {
        if (activeAttendanceTab === "attendance" && !permissions.attendance && permissions.task) setActiveAttendanceTab("task");
        if (activeAttendanceTab === "task" && !permissions.task && permissions.attendance) setActiveAttendanceTab("attendance");
    }, [activeAttendanceTab, permissions.attendance, permissions.task]);

    const garrisonDerivedCounts = useMemo(() => getGarrisonDerivedCounts(dashboardData.garrisons), [dashboardData.garrisons]);
    const filteredAttendances = useMemo(() => filterAttendances(dashboardData.attendances, attendanceFilters), [dashboardData.attendances, attendanceFilters]);
    const filteredTasks = useMemo(() => filterTasks(dashboardData.tasks, taskFilters), [dashboardData.tasks, taskFilters]);
    const filteredGarrisons = useMemo(() => filterGarrisons(dashboardData.garrisons, garrisonFilters), [dashboardData.garrisons, garrisonFilters]);
    const boName = configuration.boName || "BO";

    const escapeHtml = useCallback((value = "") =>
        String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;"), []);

    const showPendingDashboardAction = useCallback((label, reason) => {
        projectUtils.swalMessage(
            "warning",
            "Ação pendente",
            `${label}: esta ação ainda precisa ser migrada para o novo painel. ${reason}`,
            "Ok",
        );
    }, []);

    const getDashboardActionErrorMessage = useCallback((error, fallback) => {
        const message = error.response?.data?.errorMessages ?? error.response?.data?.message;
        if (Array.isArray(message)) return message.join("<br />");
        return message || fallback;
    }, []);

    const openGisItem = useCallback(itemId => {
        const encodedItemId = encodeURIComponent(itemId);
        const url = `/gis?item_id=${encodedItemId}`
        navigate(url, true);
    }, [navigate]);

    const openMosaicCameras = useCallback(cameras => {
        if (!cameras) return;

        localStorage.setItem("video-cameras", typeof cameras === "string" ? cameras : JSON.stringify(cameras));
        navigate("/mosaic", true);
    }, [navigate]);

    const generateBoFromDashboard = useCallback(garrison => {
        if (!garrison?.dispatchId) {
            showPendingDashboardAction(`Gerar ${boName}`, "O card não possui dispatchId para acionar o endpoint generateNumberBo.");
            return;
        }

        setGenerateBoPayload({garrison});
    }, [boName, showPendingDashboardAction]);

    const convertAttendanceToTask = useCallback((attendance) => {
        if (!attendance?.attendanceId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar o atendimento.", "Ok");
            return;
        }

        setAttendanceConvertTaskPayload({attendance});
    }, []);

    const moveTaskToAttendance = useCallback((task) => {
        if (!task?.id) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a tarefa.", "Ok");
            return;
        }

        setTaskMoveAttendancePayload({task});
    }, []);

    const ackGarrisonByMobile = useCallback(async (garrison) => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        try {
            await GarrisonService.ackGarrisonByMobile(garrison.garrisonId);
            projectUtils.swalMessage("success", "Sucesso", "Você informou que está ciente da criação da guarnição.", "Ok");
            cardInteractions.clearSelection();
        } catch (error) {
            console.error("Erro ao confirmar ciência da guarnição pelo dashboard:", error);
            projectUtils.swalErrorMessage("", getDashboardActionErrorMessage(error, "Não foi possível informar que está ciente da criação da guarnição."), "Ok");
        }
    }, [cardInteractions, getDashboardActionErrorMessage]);

    const ackPanic = useCallback(async (garrison) => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        try {
            await GarrisonService.ackPanic(garrison.garrisonId);
            projectUtils.swalSuccessMessage("success", "Sucesso", "Pânico desativado.", "Ok");
            cardInteractions.clearSelection();
        } catch (error) {
            console.error("Erro ao desativar pânico da guarnição pelo dashboard:", error);
            projectUtils.swalErrorMessage("", getDashboardActionErrorMessage(error, "Não foi possível desativar o estado de pânico."), "Ok");
        }
    }, [cardInteractions, getDashboardActionErrorMessage]);

    const setPanic = useCallback((garrison) => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        const garrisonName = garrison.garrisonName ?? garrison.name ?? "";

        projectUtils.swalMessage(
            "warning",
            "Ativar pânico para a guarnição?",
            `<h3>Guarnição</h3> ${escapeHtml(garrisonName)}`,
            "Sim",
            true,
            "Não",
            async ({isConfirmed}) => {
                if (!isConfirmed) return;

                try {
                    await GarrisonService.setPanic(garrison.garrisonId);
                    projectUtils.swalSuccessMessage("success", "Sucesso", "Pânico ativado.", "Ok");
                    cardInteractions.clearSelection();
                } catch (error) {
                    console.error("Erro ao ativar pânico da guarnição pelo dashboard:", error);
                    projectUtils.swalErrorMessage("", getDashboardActionErrorMessage(error, "Não foi possível ativar o estado de pânico."), "Ok");
                }
            },
            true,
            "warning",
            true
        );
    }, [cardInteractions, escapeHtml, getDashboardActionErrorMessage]);

    const openCloseTaskModal = useCallback(task => {
        setCloseTaskPayload({task});
    }, []);

    const openAttendanceInformation = useCallback(attendance => {
        setCardInformationPayload({type: "attendance", item: attendance});
    }, []);

    const openTaskInformation = useCallback(task => {
        setCardInformationPayload({type: "task", item: task});
    }, []);

    const openCloseAttendanceModal = useCallback(attendance => {
        if (!attendance?.attendanceId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar o atendimento.", "Ok");
            return;
        }
        setCloseAttendancePayload({attendance});
    }, []);

    const openGarrisonInformation = useCallback(garrison => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        setGarrisonInformationPayload({garrison});
    }, []);

    const openGarrisonHistory = useCallback(garrison => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        setGarrisonHistoryPayload({garrison});
    }, []);

    const openGarrisonAssignments = useCallback(garrison => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        setGarrisonAssignmentsPayload({garrison});
    }, []);

    const openGarrisonSchedule = useCallback(garrison => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        setGarrisonSchedulePayload({garrison});
    }, []);

    const openGarrisonDispatch = useCallback(garrison => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        setGarrisonDispatchActionPayload({garrison, mode: "dispatch"});
    }, []);

    const openGarrisonActivity = useCallback(garrison => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        setGarrisonDispatchActionPayload({garrison, mode: "activity"});
    }, []);

    const openGarrisonSupport = useCallback(garrison => {
        if (!garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição.", "Ok");
            return;
        }

        setStartSupportPayload({garrison, garrisonId: garrison.garrisonId});
    }, []);

    const openNearbyGarrisonDispatch = useCallback(({attendance, garrison}) => {
        if (!attendance?.attendanceId || !garrison?.garrisonId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar o atendimento ou a guarnição.", "Ok");
            return;
        }

        setCardInformationPayload(null);
        setStartDispatchPayload({attendance, garrison});
    }, []);

    const dashboardActionHandlers = useMemo(() => ({
        ackGarrisonByMobile,
        ackPanic,
        changeContingents: garrison => setChangeContingentsPayload({garrison}),
        changeOwner: garrison => setChangeOwnerPayload({garrison}),
        closeAttendance: openCloseAttendanceModal,
        closeDispatch: garrison => setCloseDispatchPayload({garrison}),
        closeGarrison: garrison => setCloseGarrisonPayload({garrison}),
        convertAttendanceToTask,
        closeTask: openCloseTaskModal,
        generateBo: generateBoFromDashboard,
        moveTaskToAttendance,
        openGarrisonAssignments,
        openGarrisonActivity,
        openGarrisonDispatch,
        openGarrisonHistory,
        openGarrisonInformation,
        openGarrisonSchedule,
        openGarrisonSupport,
        openGis: openGisItem,
        openMosaic: openMosaicCameras,
        pending: showPendingDashboardAction,
        setPanic,
    }), [ackGarrisonByMobile, ackPanic, convertAttendanceToTask, generateBoFromDashboard, moveTaskToAttendance, openCloseAttendanceModal, openCloseTaskModal, openGarrisonActivity, openGarrisonAssignments, openGarrisonDispatch, openGarrisonHistory, openGarrisonInformation, openGarrisonSchedule, openGarrisonSupport, openGisItem, openMosaicCameras, setPanic, showPendingDashboardAction]);

    const selectedCardActions = useMemo(
        () => buildDashboardCardActions(cardInteractions.selectedCard, {configuration, permissions, handlers: dashboardActionHandlers}),
        [cardInteractions.selectedCard, configuration, dashboardActionHandlers, permissions]
    );

    const contextMenuActions = useMemo(
        () => buildDashboardCardActions(cardInteractions.contextMenu?.card, {configuration, permissions, handlers: dashboardActionHandlers}),
        [cardInteractions.contextMenu?.card, configuration, dashboardActionHandlers, permissions]
    );

    const cardInformationHandlers = useMemo(() => ({
        openAttendanceInformation,
        openGarrisonInformation,
        openTaskInformation,
    }), [openAttendanceInformation, openGarrisonInformation, openTaskInformation]);

    const selectedGarrisonShortcutTarget = cardInteractions.selectedCard?.type === "garrison" ? cardInteractions.selectedCard.item : null;

    const isAnyDashboardModalOpen = Boolean(
        startDispatchPayload || startSupportPayload || choicePayload || assignmentPayload
        || taskPayload || closeTaskPayload || generateBoPayload || attendanceConvertTaskPayload
        || taskMoveAttendancePayload || garrisonInformationPayload || garrisonHistoryPayload
        || garrisonAssignmentsPayload || garrisonSchedulePayload || garrisonDispatchActionPayload
        || assignmentDispatchPayload || closeAttendancePayload || changeOwnerPayload
        || closeDispatchPayload || closeGarrisonPayload || changeContingentsPayload || cardInformationPayload
    );

    const garrisonShortcutsEnabled = Boolean(selectedGarrisonShortcutTarget) && !isAnyDashboardModalOpen;

    const runGarrisonShortcut = useCallback(action => {
        const activeElement = document.activeElement;
        const tag = activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || activeElement?.isContentEditable) return;
        if (!selectedGarrisonShortcutTarget) return;
        action(selectedGarrisonShortcutTarget);
    }, [selectedGarrisonShortcutTarget]);

    useGlobalKey("i", () => runGarrisonShortcut(garrison => openGarrisonInformation(garrison)), garrisonShortcutsEnabled);
    useGlobalKey("h", () => runGarrisonShortcut(garrison => openGarrisonHistory(garrison)), garrisonShortcutsEnabled);

    useGlobalKey("m", () => runGarrisonShortcut(garrison => {
        if (permissions.gis && garrison.garrisonHasMobile === true) openGisItem(`garrison-${garrison.garrisonId}`);
    }), garrisonShortcutsEnabled);

    useGlobalKey("v", () => runGarrisonShortcut(garrison => {
        if (garrison.vehicleCameraRtsp) openMosaicCameras(garrison.vehicleCameraRtsp);
    }), garrisonShortcutsEnabled);

    const dashboardContextValue = useMemo(() => ({
        configuration,
        permissions,
        lastEventAt,
        isConfigurationLoading,
        hasConfigurationError,
        cardInteractions,
        cardInformationHandlers,
        openNearbyGarrisonDispatch,
        navigate
    }), [configuration, permissions, lastEventAt, isConfigurationLoading, hasConfigurationError, cardInteractions, cardInformationHandlers, openNearbyGarrisonDispatch]);

    const setAttendanceFilter = (field, value) => {
        setAttendanceFilters(prev => ({...prev, [field]: value}));
    };

    const setTaskFilter = (field, value) => {
        setTaskFilters(prev => ({...prev, [field]: value}));
    };

    const setGarrisonFilter = (field, value) => {
        setGarrisonFilters(prev => ({...prev, [field]: value}));
    };

    const clearCardSelectionOnOutsideClick = (event) => {
        if (event.target.closest("[data-card-type]")) return;
        if (event.target.closest("[data-dashboard-card-actions]")) return;
        cardInteractions.clearSelection();
    };

    const showAssignmentsAfterCloseDispatch = async garrisonId => {
        if (garrisonId === undefined || garrisonId === "" || garrisonId == null) return;

        try {
            const assignmentData = await GarrisonService.getGarrisonByIdToView(garrisonId);
            const assignmentItems = Array.isArray(assignmentData?.assignment) ? assignmentData.assignment : [];

            if (assignmentItems.length === 0) return;

            setGarrisonAssignmentsPayload({
                garrison: {
                    garrisonId,
                    garrisonName: assignmentData?.garrison?.name ?? "",
                },
                assignmentData,
                selectForDispatch: true,
            });
        } catch (error) {
            console.error("Erro ao buscar atribuições da guarnição após encerrar despacho:", error);
            projectUtils.swalErrorMessage("Erro", "Não foi possível buscar as atribuições da guarnição", "Ok");
        }
    };

    const finishCloseAttendance = () => {
        const garrisonId = closeAttendancePayload?.afterCloseDispatchGarrisonId;
        setCloseAttendancePayload(null);

        if (garrisonId !== undefined && garrisonId !== "" && garrisonId != null) {
            void showAssignmentsAfterCloseDispatch(garrisonId);
        }
    };

    const closeStartDispatchModal = () => setStartDispatchPayload(null);
    const closeStartSupportModal = () => setStartSupportPayload(null);
    const closeChoiceModal = () => setChoicePayload(null);
    const closeAssignmentModal = () => setAssignmentPayload(null);
    const closeTaskModal = () => setTaskPayload(null);
    const closeTaskCloseModal = () => setCloseTaskPayload(null);
    const closeGenerateBoModal = () => setGenerateBoPayload(null);
    const closeCardInformationModal = () => setCardInformationPayload(null);
    const closeAttendanceConvertTaskModal = () => setAttendanceConvertTaskPayload(null);
    const closeTaskMoveAttendanceModal = () => setTaskMoveAttendancePayload(null);
    const closeGarrisonInformationModal = () => setGarrisonInformationPayload(null);
    const closeGarrisonHistoryModal = () => setGarrisonHistoryPayload(null);
    const closeGarrisonAssignmentsModal = () => setGarrisonAssignmentsPayload(null);
    const closeGarrisonScheduleModal = () => setGarrisonSchedulePayload(null);
    const closeGarrisonDispatchActionModal = () => setGarrisonDispatchActionPayload(null);
    const closeAssignmentDispatchModal = () => setAssignmentDispatchPayload(null);
    const closeAttendanceCloseModal = finishCloseAttendance;
    const closeChangeOwnerModal = () => setChangeOwnerPayload(null);
    const closeDispatchCloseModal = () => setCloseDispatchPayload(null);
    const closeGarrisonCloseModal = () => setCloseGarrisonPayload(null);
    const closeChangeContingentsModal = () => setChangeContingentsPayload(null);

    const handleStartDispatchSuccess = () => {
        setStartDispatchPayload(null);
        cardInteractions.clearSelection();
    };

    const handleStartSupportSuccess = () => {
        setStartSupportPayload(null);
        cardInteractions.clearSelection();
    };

    const handleChoiceDispatch = () => {
        if (!choicePayload) return;

        const payload = choicePayload;
        closeChoiceModal();

        if (payload.kind === "task") {
            setTaskPayload({...payload, mode: "start-task"});
            return;
        }

        setStartDispatchPayload(payload);
    };

    const handleChoiceAssignment = () => {
        if (!choicePayload) return;

        const payload = choicePayload;
        closeChoiceModal();

        if (payload.kind === "task") {
            setTaskPayload({...payload, mode: "assignment-task"});
            return;
        }

        setAssignmentPayload(payload);
    };

    const handleAssignmentSuccess = () => {
        setAssignmentPayload(null);
        cardInteractions.clearSelection();
    };

    const handleTaskSuccess = () => {
        setTaskPayload(null);
        cardInteractions.clearSelection();
    };

    const handleCloseTaskSuccess = () => {
        setCloseTaskPayload(null);
        cardInteractions.clearSelection();
    };

    const handleGenerateBoSuccess = () => {
        setGenerateBoPayload(null);
        cardInteractions.clearSelection();
    };

    const handleAttendanceConvertTaskSuccess = () => {
        setAttendanceConvertTaskPayload(null);
        cardInteractions.clearSelection();
    };

    const handleTaskMoveAttendanceSuccess = () => {
        setTaskMoveAttendancePayload(null);
        cardInteractions.clearSelection();
    };

    const handleGarrisonDispatchActionSuccess = ({dispatchToCloseAttendanceId} = {}) => {
        setGarrisonDispatchActionPayload(null);
        setGarrisonSchedulePayload(null);
        cardInteractions.clearSelection();

        if (dispatchToCloseAttendanceId) {
            setCloseAttendancePayload({attendance: {attendanceId: dispatchToCloseAttendanceId}});
        }
    };

    const handleAssignmentDispatchSuccess = () => {
        setAssignmentDispatchPayload(null);
        setGarrisonAssignmentsPayload(null);
        cardInteractions.clearSelection();
    };

    const handleCloseDispatchSuccess = ({attendanceId, garrisonId} = {}) => {
        setCloseDispatchPayload(null);
        cardInteractions.clearSelection();

        if (attendanceId !== undefined && attendanceId !== "" && attendanceId != null) {
            setCloseAttendancePayload({attendance: {attendanceId}, afterCloseDispatchGarrisonId: garrisonId});
            return;
        }

        if (garrisonId !== undefined && garrisonId !== "" && garrisonId != null) {
            void showAssignmentsAfterCloseDispatch(garrisonId);
        }
    };

    const handleCloseAttendanceSuccess = () => {
        finishCloseAttendance();
        cardInteractions.clearSelection();
    };

    const handleDestructiveSuccess = () => {
        setCloseAttendancePayload(null);
        setChangeOwnerPayload(null);
        setCloseDispatchPayload(null);
        setCloseGarrisonPayload(null);
        setChangeContingentsPayload(null);
        cardInteractions.clearSelection();
    };

    const handleSupportRequired = (payload) => {
        setStartDispatchPayload(null);
        setStartSupportPayload(payload);
    };

    const handleScheduleDispatchActivity = ({garrison, schedule, activity}) => {
        setGarrisonSchedulePayload(null);
        setGarrisonDispatchActionPayload({garrison, schedule, activity, mode: "schedule"});
    };

    const handleDispatchAssignment = assignment => {
        setGarrisonAssignmentsPayload(null);
        setAssignmentDispatchPayload({assignment});
    };

    const openNewAttendance = () => {
        const url = `${DESPACHO_WEB_URL}/attendance/form?redir=despacho/dashboard`
        navigate(url, true);
    }

    const renderAttendanceHeader = () => (
        <div className="flex w-full items-end justify-between pl-2 pt-1">
            <div className="flex items-end">
                {permissions.attendance && (
                    <div className={clsx(
                        "cursor-pointer h-7 rounded-t-[5px] border border-b-0 border-[#c8c8c8] px-2",
                        activeAttendanceTab === "attendance" ? "mb-[-1px] bg-white text-[#02486b]" : "bg-[#eef0f3] text-[#666] hover:bg-[#e0e3e8]",
                        "flex items-center justify-between gap-1"
                    )}>
                        <span className={"dashboard-section-title"} onClick={() => setActiveAttendanceTab("attendance")}>ATENDIMENTOS</span>
                        <p className={"text-[16px]"} onClick={openNewAttendance}>
                            <FaPlusSquare />
                        </p>
                    </div>
                )}
                {permissions.task && (
                    <button
                        type="button"
                        onClick={() => setActiveAttendanceTab("task")}
                        className={clsx(
                            "dashboard-section-title cursor-pointer ml-0.5 h-7 rounded-t-[5px] border border-b-0 border-[#c8c8c8] px-2",
                            activeAttendanceTab === "task" ? "mb-[-1px] bg-white text-[#02486b]" : "bg-[#eef0f3] text-[#666] hover:bg-[#e0e3e8]",
                        )}
                    >
                        TAREFAS
                    </button>
                )}
            </div>
            <DashboardCardActionBar
                card={["attendance", "task"].includes(cardInteractions.selectedCard?.type) ? cardInteractions.selectedCard : null}
                actions={selectedCardActions}
            />
        </div>
    );

    const openNewGarrison = () => {
        const url = `${DESPACHO_WEB_URL}/garrison/form?redir=despacho/dashboard`
        navigate(url, true);
    }

    const renderGarrisonHeader = () => (
        <div className="flex min-h-8 w-full items-center justify-between gap-2 px-2.5">
            <div className="flex items-center gap-1">
                <span className="dashboard-section-title m-0 text-[#676a6c]">GUARNIÇÕES</span>
                <p className={"cursor-pointer text-[16px]"} onClick={openNewGarrison}>
                    <FaPlusSquare />
                </p>
            </div>
            <div className="flex w-full justify-center">
                <DashboardCardActionBar
                    card={cardInteractions.selectedCard?.type === "garrison" ? cardInteractions.selectedCard : null}
                    actions={selectedCardActions}
                />
            </div>
        </div>
    );

    const renderAttendancePanel = () => (
        <>
            <div className={`gap-3 flex flex-wrap items-end justify-start bg-white px-1.5 py-1 ${canShowGarrisonPanel ? "" : "w-[30%]"}`}>
                <DashboardCounterGroup
                    title="Situação"
                    items={ATTENDANCE_STATUS_FILTERS}
                    counts={dashboardData.attendanceSubtitles}
                    activeValue={attendanceFilters.status}
                    onChange={value => setAttendanceFilter("status", value)}
                />
                <DashboardCounterGroup
                    title="Origem"
                    items={ATTENDANCE_SOURCE_FILTERS}
                    counts={dashboardData.attendanceSubtitles}
                    activeValue={attendanceFilters.source}
                    onChange={value => setAttendanceFilter("source", value)}
                    variant="source"
                />
                <div className="flex shrink-0 flex-col gap-1">
                    <span className="text-[13px] leading-none text-[#676a6c]">Chat</span>
                    <div className="flex gap-1">
                        <button
                            type="button"
                            title="Mensagens não lidas"
                            onClick={() => setAttendanceFilter("source", attendanceFilters.source === "dispatchHelpNotRead" ? null : "dispatchHelpNotRead")}
                            className={clsx(
                                "relative flex h-7 w-7 items-center justify-center rounded-[3px] border border-[#a6a6a6] bg-[#f5f5f5] text-[#777] cursor-pointer",
                                attendanceFilters.source === "dispatchHelpNotRead" && "shadow-[0_0_3px_2px_rgba(58,58,58,1)] opacity-90",
                            )}
                        >
                            <FaCommentAlt size={10}/>
                            {dashboardData.attendanceSubtitles.dispatchHelpNotRead > 0 ? (
                                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#ef5744]" />
                            ) : null}
                        </button>
                        <button
                            type="button"
                            title={isChatSilenced ? "Som desativado" : "Som ativado"}
                            onClick={toggleChatSilence}
                            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[3px] border border-[#a6a6a6] bg-[#f5f5f5] text-[#777]"
                        >
                            {isChatSilenced ? <FaVolumeXmark size={10}/> : <FaVolumeHigh size={10}/>}
                        </button>
                    </div>
                </div>
            </div>
            <div className={`border-b border-[#d1d1d1]`}>
                <DashboardSearch
                    className={canShowGarrisonPanel ? "" : "absolute right-0 top-3.25 w-[30%]"}
                    value={attendanceFilters.search}
                    onChange={value => setAttendanceFilter("search", value)}
                    onClear={() => setAttendanceFilters(initialCardFilters)}
                    labelId={"attendance-search"}
                />
            </div>
            <div className={"min-h-0 overflow-y-auto space-y-1 bg-white " + (canShowGarrisonPanel ? "flex-1" : "flex flex-wrap")}>
                {
                    filteredAttendances.length ?
                        filteredAttendances.map(attendance => (
                            <AttendanceCard key={attendance.attendanceId} attendance={attendance} serverDate={dashboardData.serverDate} canShowGarrisonPanel={canShowGarrisonPanel}/>
                        )
                    ) : ''
                }
            </div>
        </>
    );

    const renderTaskPanel = () => (
        <>
            <div className={`gap-3 flex flex-wrap items-end justify-start bg-white px-1.5 py-1 ${canShowGarrisonPanel ? "" : "w-[12%]"}`}>
                <DashboardCounterGroup
                    title="Situação"
                    items={TASK_STATUS_FILTERS}
                    counts={dashboardData.taskSubtitles}
                    activeValue={taskFilters.status}
                    onChange={value => setTaskFilter("status", value)}
                />
                <DashboardCounterGroup
                    title="Origem"
                    items={TASK_SOURCE_FILTERS}
                    counts={dashboardData.taskSubtitles}
                    activeValue={taskFilters.source}
                    onChange={value => setTaskFilter("source", value)}
                    variant="source"
                />
            </div>
            <div className={`border-b border-[#d1d1d1]`}>
                <DashboardSearch
                    className={canShowGarrisonPanel ? "" : "absolute right-0 top-3.25 w-[30%]"}
                    value={taskFilters.search}
                    onChange={value => setTaskFilter("search", value)}
                    onClear={() => setTaskFilters(initialCardFilters)}
                    labelId={"task-search"}
                />
            </div>
            <div className={"min-h-0 overflow-y-auto space-y-1 bg-white " + (canShowGarrisonPanel ? "flex-1" : "flex flex-wrap")}>
                {filteredTasks.length ? filteredTasks.map(task => (
                    <TaskCard key={task.id} task={task} canShowGarrisonPanel={canShowGarrisonPanel}/>
                )) : ''}
            </div>
        </>
    );

    return (
        <DashboardContext.Provider value={dashboardContextValue}>
            <Template>
                <Template.Header>
                    <Template.Title browserTitle={"CAD"}>Central de Atendimento e Despacho</Template.Title>
                    <div className="mt-1 mr-2 flex items-center gap-3">
                        <button type="button" className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[3px] border border-[#a6a6a6] text-[#777]" title="Ajuda" onClick={() => setHelpOpen(true)}>
                            <FaQuestion size={12}/>
                        </button>
                    </div>
                </Template.Header>
                <Template.Body className="flex h-[calc(100vh-53px)] min-h-0 flex-col p-1.5">

                    {!hasAnyPanel && (
                        <div className="flex h-full items-center justify-center text-sm text-[#676a6c]">
                            Você não possui permissão para visualizar o painel.
                        </div>
                    )}

                    {hasAnyPanel && (
                        <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row" onMouseDown={clearCardSelectionOnOutsideClick}>
                            {canShowAttendanceTaskPanel && (
                                <DashboardPanel
                                    header={renderAttendanceHeader()}
                                    className={clsx(canShowGarrisonPanel ? "lg:w-[24%]" : "lg:w-full")}
                                >
                                    {activeAttendanceTab === "attendance" ? renderAttendancePanel() : renderTaskPanel()}
                                </DashboardPanel>
                            )}

                            {canShowGarrisonPanel && (
                                <DashboardPanel header={renderGarrisonHeader()} className="flex-1">
                                    <SentryRow className={"border-b border-[#d1d1d1]"}>
                                        <div className={"col-span-7 flex flex-wrap items-end gap-x-3 gap-y-1 bg-white px-1.5 py-1"}>
                                            <DashboardCounterGroup
                                                title="Situação"
                                                items={GARRISON_STATUS_FILTERS}
                                                counts={dashboardData.garrisonSubtitles}
                                                activeValue={garrisonFilters.status}
                                                onChange={value => setGarrisonFilter("status", value)}
                                            />
                                            <DashboardCounterGroup
                                                title="Origem"
                                                items={GARRISON_SOURCE_FILTERS}
                                                counts={garrisonDerivedCounts}
                                                activeValue={garrisonFilters.source}
                                                onChange={value => setGarrisonFilter("source", value)}
                                                variant="source"
                                                showShortLabel={true}
                                            />
                                            <DashboardCounterGroup
                                                title="Composição"
                                                items={GARRISON_COMPOSITION_FILTERS}
                                                counts={garrisonDerivedCounts}
                                                activeValue={garrisonFilters.composition}
                                                onChange={value => setGarrisonFilter("composition", value)}
                                                variant="source"
                                                showIconLabel={true}
                                            />
                                        </div>
                                        <div className={"col-span-5 flex items-center"}>
                                            <SelectComponent
                                                classNameWrapper={"w-[calc(100vw-40px)]"}
                                                label="Função destinada"
                                                value={garrisonFilters.intendedFunction}
                                                options={GARRISON_INTENDED_FUNCTION_OPTIONS}
                                                onChange={(selected) => setGarrisonFilter("intendedFunction", selected?.value)}
                                            />
                                            <div className={"w-[calc(100vw-40px)]"}>
                                                <DashboardSearch
                                                    labelId={"garrison-search"}
                                                    value={garrisonFilters.search}
                                                    onChange={value => setGarrisonFilter("search", value)}
                                                    onClear={() => setGarrisonFilters(initialGarrisonFilters)}
                                                />
                                            </div>
                                        </div>
                                    </SentryRow>
                                    <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-[repeat(auto-fill,minmax(var(--dashboard-garrison-card-width),var(--dashboard-garrison-card-width)))] overflow-y-auto bg-white gap-2 p-1">
                                        {
                                            filteredGarrisons.length
                                                ? filteredGarrisons.map(
                                                    garrison => (
                                                        <GarrisonCard
                                                            key={`${garrison.garrisonId}-${garrison.dispatchId ?? "available"}`}
                                                            garrison={garrison}
                                                            serverDate={dashboardData.serverDate}
                                                        />
                                                    )
                                                ) : ''
                                        }
                                    </div>
                                </DashboardPanel>
                            )}
                        </div>
                    )}
                </Template.Body>
            </Template>

            <StartDispatchModal
                payload={startDispatchPayload}
                onClose={closeStartDispatchModal}
                onSuccess={handleStartDispatchSuccess}
                onSupportRequired={handleSupportRequired}
            />
            <StartSupportModal
                payload={startSupportPayload}
                onClose={closeStartSupportModal}
                onSuccess={handleStartSupportSuccess}
            />
            <DispatchChoiceModal
                open={Boolean(choicePayload)}
                onClose={closeChoiceModal}
                onDispatch={handleChoiceDispatch}
                onAssignment={handleChoiceAssignment}
            />
            <AttendanceAssignmentModal
                payload={assignmentPayload}
                onClose={closeAssignmentModal}
                onSuccess={handleAssignmentSuccess}
            />
            <TaskActionModal
                payload={taskPayload}
                onClose={closeTaskModal}
                onSuccess={handleTaskSuccess}
            />
            <CloseTaskModal
                payload={closeTaskPayload}
                onClose={closeTaskCloseModal}
                onSuccess={handleCloseTaskSuccess}
            />
            <GenerateBoModal
                payload={generateBoPayload}
                boName={boName}
                onClose={closeGenerateBoModal}
                onSuccess={handleGenerateBoSuccess}
            />
            <DashboardCardInformationModal
                payload={cardInformationPayload}
                boName={boName}
                onClose={closeCardInformationModal}
            />
            <AttendanceConvertTaskModal
                payload={attendanceConvertTaskPayload}
                onClose={closeAttendanceConvertTaskModal}
                onSuccess={handleAttendanceConvertTaskSuccess}
            />
            <TaskMoveAttendanceModal
                payload={taskMoveAttendancePayload}
                onClose={closeTaskMoveAttendanceModal}
                onSuccess={handleTaskMoveAttendanceSuccess}
            />
            <GarrisonInformationModal
                payload={garrisonInformationPayload}
                boName={boName}
                onClose={closeGarrisonInformationModal}
            />
            <GarrisonHistoryModal
                payload={garrisonHistoryPayload}
                boName={boName}
                onClose={closeGarrisonHistoryModal}
            />
            <GarrisonAssignmentsModal
                payload={garrisonAssignmentsPayload}
                onClose={closeGarrisonAssignmentsModal}
                onDispatchAssignment={handleDispatchAssignment}
            />
            <GarrisonScheduleModal
                payload={garrisonSchedulePayload}
                onClose={closeGarrisonScheduleModal}
                onDispatchActivity={handleScheduleDispatchActivity}
            />
            <GarrisonDispatchActionModal
                payload={garrisonDispatchActionPayload}
                onClose={closeGarrisonDispatchActionModal}
                onSuccess={handleGarrisonDispatchActionSuccess}
            />
            <AssignmentDispatchModal
                payload={assignmentDispatchPayload}
                onClose={closeAssignmentDispatchModal}
                onSuccess={handleAssignmentDispatchSuccess}
            />
            <CloseAttendanceModal
                payload={closeAttendancePayload}
                onClose={closeAttendanceCloseModal}
                onSuccess={handleCloseAttendanceSuccess}
            />
            <ChangeOwnerModal
                payload={changeOwnerPayload}
                boName={boName}
                onClose={closeChangeOwnerModal}
                onSuccess={handleDestructiveSuccess}
            />
            <CloseDispatchModal
                payload={closeDispatchPayload}
                onClose={closeDispatchCloseModal}
                onSuccess={handleCloseDispatchSuccess}
            />
            <CloseGarrisonModal
                payload={closeGarrisonPayload}
                onClose={closeGarrisonCloseModal}
                onSuccess={handleDestructiveSuccess}
            />
            <ChangeContingentsModal
                payload={changeContingentsPayload}
                onClose={closeChangeContingentsModal}
                onSuccess={handleDestructiveSuccess}
            />
            <DashboardCardContextMenu
                menu={cardInteractions.contextMenu}
                actions={contextMenuActions}
                onClose={cardInteractions.closeContextMenu}
            />
            <DashboardHelpModal open={helpOpen} setOpen={setHelpOpen} permissions={permissions} boName={configuration.boName}/>
        </DashboardContext.Provider>
    );
};

export default Dashboard;
